import { useState } from "react";
import { Check, Sparkles, Rocket, Star, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PillButton } from "./PillButton";
import { cn } from "@/lib/utils";

type Plan = {
  name: string;
  price: { monthly: number; yearly: number };
  Icon: typeof Sparkles;
  features: string[];
  cta: string;
  ctaHref: string;
  highlight?: boolean;
};

const plans: Plan[] = [
  {
    name: "Starter",
    Icon: Sparkles,
    price: { monthly: 39, yearly: 31 },
    features: ["100 submissions/month", "2 department channels", "Basic sentiment AI", "Email support"],
    cta: "Get Started",
    ctaHref: "/auth?mode=signup",
  },
  {
    name: "Professional",
    Icon: Rocket,
    price: { monthly: 79, yearly: 63 },
    features: [
      "Unlimited submissions",
      "10 department channels",
      "Advanced AI insights & alerts",
      "HR response templates",
      "Priority support",
    ],
    cta: "Get Started",
    ctaHref: "/auth?mode=signup",
    highlight: true,
  },
  {
    name: "Business",
    Icon: Star,
    price: { monthly: 149, yearly: 119 },
    features: [
      "Everything in Professional",
      "White-label for your brand",
      "HRIS integrations",
      "Executive dashboards",
      "4-hour support SLA",
    ],
    cta: "Get Started",
    ctaHref: "/auth?mode=signup",
  },
  {
    name: "Enterprise",
    Icon: Building2,
    price: { monthly: 0, yearly: 0 },
    features: [
      "Everything in Business",
      "SSO / SAML",
      "Dedicated CSM",
      "Custom contract & DPA",
      "Private deployment",
    ],
    cta: "Book a Call",
    ctaHref: "/contact",
  },
];

export const PricingTableV2 = () => {
  const navigate = useNavigate();
  const [yearly, setYearly] = useState(false);

  return (
    <section className="container mx-auto px-3 lg:px-8 py-16 lg:py-24">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight">Simple Pricing For Everyone</h2>
        <p className="mt-4 text-lg text-muted-foreground">
          Flat monthly pricing. No per-seat fees. Always know what you'll pay.
        </p>

        <div className="mt-8 inline-flex items-center gap-3 p-1.5 rounded-full bg-muted">
          <button
            onClick={() => setYearly(false)}
            className={cn(
              "px-5 py-2 rounded-full text-sm font-medium transition",
              !yearly ? "bg-[hsl(var(--mkt-cta))] text-white" : "text-foreground/70"
            )}
          >
            Monthly
          </button>
          <button
            onClick={() => setYearly(true)}
            className={cn(
              "px-5 py-2 rounded-full text-sm font-medium transition flex items-center gap-2",
              yearly ? "bg-[hsl(var(--mkt-cta))] text-white" : "text-foreground/70"
            )}
          >
            Yearly
            <span className={cn(
              "text-[10px] px-2 py-0.5 rounded-full font-semibold",
              yearly ? "bg-white/20 text-white" : "bg-[hsl(var(--mkt-primary)/0.15)] text-[hsl(var(--mkt-primary-deep))]"
            )}>
              –20%
            </span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto">
        {plans.map((p) => {
          const Icon = p.Icon;
          const isCustom = p.price.monthly === 0;
          return (
            <div
              key={p.name}
              className={cn(
                "relative rounded-3xl p-7 lg:p-8 flex flex-col border transition",
                p.highlight
                  ? "mkt-gradient-hero text-white border-transparent mkt-shadow scale-[1.02]"
                  : "bg-white border-border mkt-shadow-card"
              )}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white text-[hsl(var(--mkt-primary-deep))] text-[11px] font-bold tracking-wide shadow-md">
                  MOST TEAMS PICK THIS
                </div>
              )}
              <div className={cn(
                "h-12 w-12 rounded-2xl inline-flex items-center justify-center mb-5",
                p.highlight ? "bg-white/20" : "bg-[hsl(var(--mkt-primary)/0.10)] text-[hsl(var(--mkt-primary))]"
              )}>
                <Icon className="h-5 w-5" />
              </div>

              <h3 className={cn("text-xl font-bold", p.highlight ? "text-white" : "text-foreground")}>
                {p.name}
              </h3>

              <div className="mt-3 flex items-baseline gap-1">
                {isCustom ? (
                  <span className="text-4xl font-extrabold">Custom</span>
                ) : (
                  <>
                    <span className="text-5xl font-extrabold tracking-tight">
                      ${yearly ? p.price.yearly : p.price.monthly}
                    </span>
                    <span className={cn("text-sm", p.highlight ? "text-white/80" : "text-muted-foreground")}>
                      /mo
                    </span>
                  </>
                )}
              </div>

              <button
                onClick={() => navigate(p.ctaHref)}
                className={cn(
                  "mt-6 h-12 w-full rounded-full font-medium transition",
                  p.highlight
                    ? "bg-white text-[hsl(var(--mkt-primary-deep))] hover:bg-white/95"
                    : "bg-[hsl(var(--mkt-cta))] text-white hover:bg-[hsl(240_10%_15%)]"
                )}
              >
                {p.cta}
              </button>

              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className={cn(
                      "h-4 w-4 mt-0.5 shrink-0",
                      p.highlight ? "text-white" : "text-[hsl(var(--mkt-primary))]"
                    )} />
                    <span className={p.highlight ? "text-white/95" : "text-foreground/80"}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
};
